/**
 * FlowNotes API Client - Account
 * Account email management functions
 */

import { fetchWithAuth } from './core';
import { API_BASE_URL } from './core';

/** Secondary / additional account emails (GET /api/account/emails). */
export interface AccountEmailRow {
  id: string
  email: string
  isPrimary: boolean
  verified: boolean
  preferredOrgId: string | null
  verificationPending: boolean
  verificationExpiresAt: string | null
  createdAt: string
}

export interface AccountEmailsResponse {
  success: boolean
  primary: { email: string | null; source: string; verified: boolean }
  emails: AccountEmailRow[]
}

export async function fetchAccountEmails(): Promise<AccountEmailsResponse> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/emails`)
  const data = (await r.json()) as AccountEmailsResponse & { error?: string }
  if (!r.ok) throw new Error(data.error || r.statusText)
  return data
}

/** Human-readable copy for /api/account/emails error codes. */
export function formatAccountEmailError(code: string | undefined | null): string {
  switch (code) {
    case 'email_taken':
      return 'This email is already linked to another Maater account. Sign in with that address, or ask support to merge the accounts.'
    case 'email_pending_other':
      return 'This email has a pending verification on another account. Ask them to cancel it, or try again later.'
    case 'same_as_primary':
      return 'That is already your primary email.'
    case 'invalid_email':
      return 'Enter a valid email address.'
    case 'org_not_accessible':
      return 'You do not have access to that preferred workspace.'
    case 'rate_limit_user':
    case 'rate_limit_ip':
      return 'Too many attempts. Wait a minute and try again.'
    case 'database_unavailable':
      return 'Could not reach the server. Try again shortly.'
    default:
      return code && !code.includes(' ') ? 'Could not add that email. Try again.' : code || 'Request failed'
  }
}

export async function postAccountEmail(
  email: string,
  preferredOrgId?: string | null
): Promise<{ success: boolean; verifyUrl?: string; error?: string; message?: string }> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/emails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      preferredOrgId: preferredOrgId ?? null,
    }),
  })
  const data = (await r.json()) as { success: boolean; verifyUrl?: string; error?: string; message?: string }
  if (!r.ok) {
    throw new Error(data.message || formatAccountEmailError(data.error) || r.statusText)
  }
  return data
}

export async function deleteAccountEmail(id: string): Promise<{ success: boolean; error?: string }> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/emails/${id}`, { method: 'DELETE' })
  const data = (await r.json()) as { success: boolean; error?: string }
  if (!r.ok) throw new Error(data.error || r.statusText)
  return data
}

export async function putAccountEmailPreferredOrg(
  id: string,
  preferredOrgId: string | null
): Promise<{ success: boolean; error?: string }> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/emails/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ preferredOrgId }),
  })
  const data = (await r.json()) as { success: boolean; error?: string }
  if (!r.ok) throw new Error(data.error || r.statusText)
  return data
}

export async function putAccountEmailPromote(id: string): Promise<{ success: boolean; primaryEmail?: string; error?: string }> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/emails/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ promote: true }),
  })
  const data = (await r.json()) as { success: boolean; primaryEmail?: string; error?: string }
  if (!r.ok) throw new Error(data.error || r.statusText)
  return data
}

export interface AccountProfileResponse {
  success: boolean
  email: string | null
  fullName: string | null
  /** Workspace Desk folder slug (`users/{deskSlug}/`). */
  deskSlug?: string | null
  needsPassword?: boolean
}

export async function fetchAccountProfile(): Promise<AccountProfileResponse> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/profile`)
  const data = (await r.json()) as AccountProfileResponse & { error?: string }
  if (!r.ok) throw new Error(data.error || r.statusText)
  return data
}

export async function patchAccountProfile(fullName: string): Promise<AccountProfileResponse> {
  const r = await fetchWithAuth(`${API_BASE_URL}/api/account/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fullName }),
  })
  const data = (await r.json()) as AccountProfileResponse & { error?: string }
  if (!r.ok) throw new Error(data.error || r.statusText)
  return data
}
